import { useEffect, useRef, useState } from 'react';
import { PROVERBS } from '@/lib/Proverbs';
import './ProverbOfTheMoment.css';

// A rotating, thought-provoking African proverb shown in the footer on every
// page. Each proverb cross-fades with a gentle upward drift and a subtle
// accent underline, honouring the continent's oral wisdom as a design motif.
// Selection is a full shuffle (Fisher–Yates) so every entry is seen once
// before the sequence repeats, with a final random re-shuffle to avoid a
// predictable loop boundary.
const ROTATE_MS = 14000;

export function ProverbOfTheMoment() {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const timer = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const order = useRef<number[]>([]);
  const pos = useRef(0);

  useEffect(() => {
    const shuffle = () => {
      const next = PROVERBS.map((_, i) => i);
      for (let i = next.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [next[i], next[j]] = [next[j], next[i]];
      }
      return next;
    };
    order.current = shuffle();
    pos.current = 0;
    setIndex(order.current[0]);

    timer.current = setInterval(() => {
      setVisible(false);
      // Wait for the fade-out, then swap and fade the next one in.
      window.setTimeout(() => {
        pos.current += 1;
        if (pos.current >= order.current.length) {
          order.current = shuffle();
          pos.current = 0;
        }
        setIndex(order.current[pos.current]);
        setVisible(true);
      }, 700);
    }, ROTATE_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, []);

  const proverb = PROVERBS[index];

  return (
    <section className="bp-proverb" aria-label="African proverb of the moment">
      <div className="bp-proverb-mark" aria-hidden="true">
        <span className="bp-proverb-quote">“</span>
      </div>
      <div
        className="bp-proverb-body"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(8px)' }}
      >
        <p className="bp-proverb-text">{proverb.proverb}</p>
        <p className="bp-proverb-meaning">{proverb.meaning}</p>
        <p className="bp-proverb-origin">
          {proverb.origin.culture}
          {proverb.origin.region ? ` · ${proverb.origin.region}` : ''}
        </p>
      </div>
    </section>
  );
}
